import { ɵɵdefineInjectable, Injectable, Component, NgModule } from '@angular/core';
import { has } from '@blue/services/permission';
import { getRoute } from '@blue/services/environment';
import { from, of } from 'rxjs';
import { map } from 'rxjs/operators';

import * as ɵngcc0 from '@angular/core';
class SessionsService {
    constructor() {
        this.routingTable = {};
        this.forceIntDevValue = -1;
        this.ustaxApp = 'USTax';
        this._coreServicesName = 'CoreServices';
        this._taxServicesName = 'TaxServices';
        this._printServicesName = 'PrintServices';
        this._connectServicesName = 'ConnectServices';
        this._usTaxServicesName = 'USTaxServices';
    }
    get taxServicesRoute() {
        return this._taxServicesRoute;
    }
    get taxCenterServicesRoute() {
        return this._taxCenterServicesRoute;
    }
    get printServicesRoute() {
        return this._printServicesRoute;
    }
    get clientCenterServicesRoute() {
        return this._clientCenterServicesRoute;
    }
    get staffApplicationRoute() {
        return this._staffApplicationRoute;
    }
    get usTaxServicesRoute() {
        return this._usTaxServicesRoute;
    }
    get connectServicesRoute() {
        return this._connectServicesRoute;
    }
    get coreServicesRoute() {
        return this._coreServicesRoute;
    }
    get profileServicesRoute() {
        return this._profileServicesRoute;
    }
    get coreServicesName() {
        return this._coreServicesName;
    }
    get taxServicesName() {
        return this._taxServicesName;
    }
    get printServicesName() {
        return this._printServicesName;
    }
    get connectServicesName() {
        return this._connectServicesName;
    }
    get usTaxServicesName() {
        return this._usTaxServicesName;
    }
    get udsToken() {
        return this._udstoken;
    }
    set udsToken(token) {
        this._udstoken = token;
    }
    get companyId() {
        return this._companyid;
    }
    set companyId(companyId) {
        this._companyid = companyId;
    }
    get contactId() {
        return this._contactid;
    }
    set contactId(contactId) {
        this._contactid = contactId;
    }
    get isTestFirm() {
        return this._istestfirm;
    }
    set isTestFirm(istestfirm) {
        this._istestfirm = istestfirm;
    }
    get mfaPassed() {
        return this._mfapassed;
    }
    set mfaPassed(mfapassed) {
        this._mfapassed = mfapassed;
    }
    get staffId() {
        return this._staffid;
    }
    set staffId(staffid) {
        this._staffid = staffid;
    }
    get staffUpdatedLast() {
        return this._staffupdatedlast;
    }
    set staffUpdatedLast(updated) {
        this._staffupdatedlast = updated;
    }
    /**
     * @description
     * Set the state of all common routes
     * @param routing table dictionary used to set the common route variables
     */
    setRoutes(routingTable) {
        this.routingTable = routingTable;
        this._taxServicesRoute = this.stripDomainName(routingTable.TaxServices);
        this._usTaxServicesRoute = this.stripDomainName(routingTable.USTaxServices);
        this._printServicesRoute = this.stripDomainName(routingTable.PrintServices);
        this._connectServicesRoute = this.stripDomainName(routingTable.ConnectServices);
        this._coreServicesRoute = this.stripDomainName(routingTable.CoreServices);
        this._profileServicesRoute = this.stripDomainName(routingTable.ProfileServices);
        this._taxCenterServicesRoute = this.stripDomainName(routingTable.TaxCenterServices);
        this._clientCenterServicesRoute = this.stripDomainName(routingTable.ClientCenterServices);
        this._staffApplicationRoute = routingTable.StaffApplication;
    }
    /**
     * @description
     * Build up a new route based on the base route, replacing all url parameters embedded
     * in the string using parenthesis.
     * @param routeBase the route base
     * @param route to use, with embedded params
     * @param urlParameters mapping the paramater name to its value
     *
     * @returns A string of the newly built route
     */
    buildRoute(routeBase, route, urlParameters = {}) {
        for (const paramName in urlParameters) {
            if (urlParameters.hasOwnProperty(paramName)) {
                if (route.indexOf(paramName) === -1) {
                    throw new Error(`The url parameter "${paramName}" was not found in route "${route}"`);
                }
                route = route.replace(`{${paramName}}`, urlParameters[paramName]);
            }
        }
        if (route.indexOf('{') !== -1 || route.indexOf('}') !== -1) {
            throw new Error(`The url parameters for route "${route}" have not been properly replaced`);
        }
        return `${routeBase}${route}`;
    }
    /**
     * @description
     * Get the route from the populated routing table.  Only use if the routing table has already been populated by
     * your application.
     * @param routeName - name of the route in the routing table
     *
     * @returns A string of the route
     */
    getSessionBindingRoute(routeName) {
        return this.stripDomainName(this.routingTable[routeName]);
    }
    /**
     * @description
     * Get the session bindings route from the routing table for a given application/entity/juris product and
     * strip the domain name by dropping the first 3 segments of a route delimited by '/'
     * (i.e. https://onvio/api/print/ -> /api/print).  Only use if the application has already populated the routing table.
     * @param application for building the product route
     * @param entity for building the product route
     * @param juris for building the product route
     *
     * @returns A string with the session bindings product route
     */
    getProductBindingRoute(application, entity, juris) {
        return this.stripDomainName(this.routingTable[`${application}${entity}${juris.toUpperCase()}Services`]);
    }
    /**
     * @description
     * Get the session bindings route and
     * strip the domain name by dropping the first 3 segments of a route delimited by '/'
     * (i.e. https://onvio/api/print/ -> /api/print)
     * @param serviceName for mapping to the route in the session bindings table
     *
     * @returns An observable with the base session bindings route
     */
    getPath(serviceName) {
        // strip the domain name by dropping the first 3 segments of a route delimited by '/'
        return from(getRoute(serviceName)).pipe(map(route => this.stripDomainName(route)));
    }
    /**
     * @description
     * Get the session bindings route for a given application/entity/juris product and
     * strip the domain name by dropping the first 3 segments of a route delimited by '/'
     * (i.e. https://onvio/api/print/ -> /api/print)
     * @param application for building the product route
     * @param entity for building the product route
     * @param juris for building the product route
     *
     * @returns An observable with the session bindings product route
     */
    getProductPath(application, entity, juris) {
        if (juris.toLowerCase() === 'au') {
            // get the path and append the juridiction to generate the Product Url for AU.
            // Example. "AUTax" + "ITR" + "Services" = AUTaxITRServices.
            if (entity.toLowerCase() === 'as') {
                return this.getPath(`${application}Services`).pipe(map(data => data + '/' + entity + '/' + juris.toUpperCase()));
            }
            // User Story #2268206 Removed casing for AU as some jsons are load from the webtool other specs.
            return this.getPath(`${application}${entity}Services`).pipe(map(data => data + '/' + juris));
        }
        else {
            return this.getPath(`${application}${entity}${juris.toUpperCase()}Services`);
        }
    }
    /**
     * @description
     * Returns a boolean observable indicating if the given frp name has been granted permission
     * @param frp - The feature resource permission identifier
     *
     * @returns An observable boolean, returning true if permission granted
     */
    getPermission(frp) {
        return from(has(frp));
    }
    /**
     * @description
     * Get the debug mode FRP
     *
     * @returns An observable with debug mode set to true or false
     */
    getDebugMode() {
        return this.getPermission('Internal.DebugMode');
    }
    /**
     * @description
     * Get the current development FRP
     *
     * @returns An observable with current development set to true or false
     */
    getInternalDevMode() {
        if (this.forceIntDevValue === -1) {
            const storedValue = JSON.parse(window.sessionStorage.getItem('bm.tax.debug.forceIntDev'));
            if (storedValue) {
                this.forceIntDevValue = storedValue.data;
            }
        }
        if (this.forceIntDevValue !== -1) {
            return of(this.forceIntDevValue === 1);
        }
        return this.getPermission('Internal.CurrentDevelopment');
    }
    /**
     * @description
     * Force the internal dev mode, bypassing the FRP
     * @param forceValue - the force value
     */
    forceInternalDevMode(forceValue) {
        this.forceIntDevValue = forceValue === true ? 1 : 0;
        window.sessionStorage.setItem('bm.tax.debug.forceIntDev', JSON.stringify({ data: this.forceIntDevValue }));
        // tslint:disable-next-line: deprecation
        window.location.reload(true);
    }
    /**
     * @description
     * Get the emerging markets FRP (USTax.EmergingAccounts)
     *
     * @returns An observable with the emerging accounts permission set to true or false
     */
    getEmergingAccounts() {
        return this.getPermission(this.ustaxApp + '.EmergingAccounts');
    }
    /**
     * @description
     * Get the Invoice from FRP (USTax.Invoice)
     *
     * @returns An observable with Invoice set to true or false
     */
    getInvoicePermission() {
        return this.getPermission(this.ustaxApp + '.Invoice');
    }
    /**
     * @description
     * Get the tax center FRP (TaxCenter.Tax)
     *
     * @returns An observable with tax cetner permission set to true or false
     */
    getTaxCenterPermission() {
        /* this FRP doesn't exist yet, so let's always allow permission to Tax Center for now */
        return of(true);
        // return this.getPermission('TaxCenter.Tax');
    }
    /**
     * @description
     * Get the application efile FRP (USTax.ELF)
     *
     * @returns An observable with application efile permission set to true or false
     */
    getApplicationElfPermission() {
        return this.getPermission(this.ustaxApp + '.ELF');
    }
    /**
     * @description
     * Get the application PII FRP (Settings.DisplayPII)
     *
     * @returns An observable with application personal information permission set to true or false
     */
    getApplicationPIIPermission() {
        return this.getPermission('Settings.DisplayPII');
    }
    /**
     * @description
     * Get the fixed assets FRP (USTax.FixedAssetsDetail)
     *
     * @returns An observable with fixed assets permission set to true or false
     */
    isFixedAssetsAllowed() {
        return this.getPermission(this.ustaxApp + '.FixedAssetsDetail');
    }
    /**
     * @description
     * Get the Setup FRP (USTax.Setup)
     *
     * @returns An observable with setup permission set to true or false
     */
    isSetupAllowed() {
        return this.getPermission(this.ustaxApp + '.Setup');
    }
    /**
     * @description
     * Get the Setup FRP (USTax.UnlockReturn)
     *
     * @returns An observable with unlock return permission set to true or false
     */
    isReturnUnlockedAllowed() {
        return this.getPermission(this.ustaxApp + '.UnlockReturn');
    }
    /**
     * @description
     * Get the products FRP (USTax.{Entity}US)
     * @param entity - the entity identifier
     *
     * @returns An observable with the product permission set to true or false
     */
    isProductAllowed(entity) {
        return this.getPermission(`${this.ustaxApp}.${entity}US`);
    }
    /**
     * @description
     * Get the product year FRP (USTax.{Year}{Entity}{Juris})
     * @param year - the product year identifier
     * @param entity - the entity identifier
     * @param juris - the juris identifier
     *
     * @returns An observable with the product year permission set to true or false
     */
    isProductYearAllowed(year, entity, juris) {
        return this.getPermission(`${this.ustaxApp}.${year}${entity}${juris}`);
    }
    /**
     * @description
     * strip the domain name by dropping the first 3 segments of a route delimited by '/'
     * @param route - the route to be modified
     *
     * @returns A route string with the domain stripped
     */
    stripDomainName(route) {
        let routeSegments = route.split('/');
        routeSegments = routeSegments.slice(3, routeSegments.length);
        return '/' + routeSegments.join('/');
    }
}
SessionsService.ɵfac = function SessionsService_Factory(t) { return new (t || SessionsService)(); };
SessionsService.ɵprov = ɵɵdefineInjectable({ factory: function SessionsService_Factory() { return new SessionsService(); }, token: SessionsService, providedIn: "root" });
SessionsService.ctorParameters = () => [];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(SessionsService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return []; }, null); })();

class SessionsComponent {
    constructor() { }
    ngOnInit() { }
}
SessionsComponent.ɵfac = function SessionsComponent_Factory(t) { return new (t || SessionsComponent)(); };
SessionsComponent.ɵcmp = /*@__PURE__*/ ɵngcc0.ɵɵdefineComponent({ type: SessionsComponent, selectors: [["bm-tax-sessions"]], decls: 2, vars: 0, template: function SessionsComponent_Template(rf, ctx) { if (rf & 1) {
        ɵngcc0.ɵɵelementStart(0, "p");
        ɵngcc0.ɵɵtext(1, " bm-tax-sessions works! ");
        ɵngcc0.ɵɵelementEnd();
    } }, encapsulation: 2 });
SessionsComponent.ctorParameters = () => [];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(SessionsComponent, [{
        type: Component,
        args: [{
                selector: 'bm-tax-sessions',
                template: `
    <p>
      bm-tax-sessions works!
    </p>
  `
            }]
    }], function () { return []; }, null); })();

class SessionsModule {
}
SessionsModule.ɵfac = function SessionsModule_Factory(t) { return new (t || SessionsModule)(); };
SessionsModule.ɵmod = /*@__PURE__*/ ɵngcc0.ɵɵdefineNgModule({ type: SessionsModule });
SessionsModule.ɵinj = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjector({ imports: [[]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(SessionsModule, [{
        type: NgModule,
        args: [{
                declarations: [SessionsComponent],
                imports: [],
                exports: [SessionsComponent]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(SessionsModule, { declarations: [SessionsComponent], exports: [SessionsComponent] }); })();

/*
 * Public API Surface of sessionservice
 */

/**
 * Generated bundle index. Do not edit.
 */

export { SessionsComponent, SessionsModule, SessionsService };

